import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import CertificateCarousel from '../../components/CertificateCarousel/CertificateCarousel';
import './About.css';

const About = () => {
  const stats = [
    { number: '1000+', label: 'Solar Installations', icon: 'fas fa-solar-panel' },
    { number: '250+', label: 'Happy Customers', icon: 'fas fa-users' },
    { number: '10+', label: 'Years Experience', icon: 'fas fa-calendar-alt' },
    { number: '50MW+', label: 'Total Capacity Installed', icon: 'fas fa-bolt' }
  ];

  const expertise = [
    {
      title: 'Residential Solar',
      description: 'Complete solar solutions for homes with premium quality panels and smart inverters.',
      technologies: ['Monocrystalline Panels', 'Smart Inverters', 'Net Metering', 'Battery Storage', 'Monitoring Systems'],
      icon: 'fas fa-home'
    },
    {
      title: 'Commercial Solar',
      description: 'Large-scale solar installations for businesses and industrial facilities.',
      technologies: ['Grid-Tie Systems', 'SCADA Monitoring', 'Power Purchase Agreements', 'Energy Management', 'Load Analysis'],
      icon: 'fas fa-building'
    },
    {
      title: 'Solar Maintenance',
      description: 'Comprehensive maintenance and monitoring services for optimal performance.',
      technologies: ['Performance Monitoring', 'Preventive Maintenance', 'Cleaning Services', 'Inverter Servicing', '24/7 Support'],
      icon: 'fas fa-tools'
    },
    {
      title: 'Energy Storage',
      description: 'Advanced battery storage solutions for energy independence and backup power.',
      technologies: ['Lithium-ion Batteries', 'Hybrid Inverters', 'Energy Management Systems', 'Grid Backup', 'Load Balancing'],
      icon: 'fas fa-battery-full'
    }
  ];

  const values = [
    {
      title: 'Innovation',
      description: 'We use the latest solar technology and smart inverters to provide cutting-edge renewable energy solutions.',
      icon: 'fas fa-lightbulb'
    },
    {
      title: 'Quality',
      description: 'We use only premium Australian technology smart inverters and Make in India solar panels for maximum efficiency.',
      icon: 'fas fa-award'
    },
    {
      title: 'Sustainability',
      description: 'We are committed to creating a sustainable future through clean, renewable solar energy solutions.',
      icon: 'fas fa-leaf'
    },
    {
      title: 'Integrity',
      description: 'We conduct business with honesty, transparency, and ethical practices.',
      icon: 'fas fa-shield-alt'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Global Solar Energy - Leading Solar Solutions Provider</title>
        <meta name="description" content="Learn about Global Solar Energy, Gujarat's leading solar energy company since 2015. We provide premium solar solutions with Australian technology and Make in India panels." />
        <meta name="keywords" content="about global solar energy, solar company gujarat, solar panels, renewable energy, solar installation" />
      </Helmet>

      <div className="about-page">
        {/* Hero Banner */}
        <section className="about-hero">
          <div className="about-hero-overlay"></div>
          <div className="about-hero-content">
            <h1 className="about-hero-title">About</h1>
          </div>
        </section>

        {/* Company Description */}
        <section className="section about-intro">
          <div className="container">
            <div className="about-intro-content">
              <div className="intro-text">
                <h2>Who We Are</h2>
                <p>
                  Global Solar Energy was founded in 2015 with the objective of designing,
                  manufacturing, and distributing world-class, high-efficiency solar products.
                  We are committed to being the global leader in innovation, design, engineering,
                  and manufacturing of solar energy solutions.
                </p>
                <p>
                  Based in Gujarat, we have grown from a small startup to a trusted partner
                  for residential and commercial solar installations across India. Our commitment
                  to excellence, premium Australian technology smart inverters, and Make in India
                  solar panels have earned us a reputation as one of the most reliable solar
                  energy providers in the region.
                </p>
                <p>
                  We believe in the power of solar energy to transform lives and create a
                  sustainable future. Our mission is to make clean, renewable energy accessible
                  to everyone, helping reduce electricity bills and carbon footprint.
                </p>
              </div>
            </div>
          </div>
        </section>
{/* Message from the Director */}
        <section className="section director-message-section">
          <div className="container">
            <div className="section-header text-center">
              <h2>Message from the Director</h2>
            </div>
            <div className="director-message-content">
              <div className="director-text">
                <p>
                  At Global Solar Energy, our vision is rooted in delivering sustainable, reliable, and future-ready solar solutions that contribute meaningfully to a cleaner environment and a stronger energy ecosystem. Since our inception, we have remained committed to quality, innovation, and customer satisfaction as the core pillars of our organization.
                </p>
                <p>
                  The global shift toward renewable energy is not just an opportunity—it is a responsibility. At Global Solar Energy, we strive to support this transition by offering efficient solar technologies, professional execution, and transparent business practices. Our focus is not limited to project delivery; we aim to build long-term relationships based on trust, performance, and shared growth.
                </p>
                <p>
                  I take pride in our dedicated team, whose technical expertise and ethical approach enable us to consistently meet and exceed client expectations. As we move forward, we will continue to invest in advanced technologies, skill development, and sustainable practices to create lasting value for our customers and stakeholders.
                </p>
                <p>
                  Thank you for placing your trust in Global Solar Energy. Together, let us power a brighter, greener future.
                </p>
              </div>
              <div className="director-image-wrapper">
                <div className="director-image">
                  <img src="/images/about/director.jpeg" alt="Mr. Sagar Vakoter - Director, Global Solar Energy" loading="lazy" />
                </div>
                <div className="director-signature">
                  <p className="signature-name">Mr. Sagar Vakoter</p>
                  <p className="signature-title">Director, Global Solar Energy</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Journey Timeline */}
        <section className="section journey-section">
          <div className="container">
            <div className="section-header text-center">
              <div className="section-badge">
                <i className="fas fa-route"></i>
                <span>Our Journey</span>
              </div>
              <h2>Our Growth Story</h2>
              <p>From a small startup to a leading solar energy provider</p>
            </div>
            <div className="timeline">
              <div className="timeline-item" data-aos="fade-up">
                <div className="timeline-year">2015</div>
                <div className="timeline-content">
                  <h4>Company Established</h4>
                  <p>Global Solar Energy founded with a vision to provide clean energy solutions</p>
                </div>
              </div>
              <div className="timeline-item" data-aos="fade-up" data-aos-delay="100">
                <div className="timeline-year">2016</div>
                <div className="timeline-content">
                  <h4>1 MW Achieved</h4>
                  <p>Reached our first milestone of 1 MW total installations</p>
                </div>
              </div>
              <div className="timeline-item" data-aos="fade-up" data-aos-delay="200">
                <div className="timeline-year">2017</div>
                <div className="timeline-content">
                  <h4>MNRE Registration</h4>
                  <p>Officially registered with the Ministry of New and Renewable Energy</p>
                </div>
              </div>
              <div className="timeline-item" data-aos="fade-up" data-aos-delay="300">
                <div className="timeline-year">2018</div>
                <div className="timeline-content">
                  <h4>Expanded to Other States</h4>
                  <p>Extended operations beyond Gujarat to other states</p>
                </div>
              </div>
              <div className="timeline-item" data-aos="fade-up" data-aos-delay="400">
                <div className="timeline-year">2019</div>
                <div className="timeline-content">
                  <h4>Commercial Entry & GUVNL Registration</h4>
                  <p>Entered the commercial sector and registered with Gujarat Urja Vikas Nigam Limited</p>
                </div>
              </div>
              <div className="timeline-item" data-aos="fade-up" data-aos-delay="500">
                <div className="timeline-year">2020</div>
                <div className="timeline-content">
                  <h4>2 MW Achieved</h4>
                  <p>Doubled our capacity and achieved the 2 MW installations milestone</p>
                </div>
              </div>
              <div className="timeline-item" data-aos="fade-up" data-aos-delay="600">
                <div className="timeline-year">2021</div>
                <div className="timeline-content">
                  <h4>Further State Expansion</h4>
                  <p>Further expansion to multiple states across India</p>
                </div>
              </div>
              <div className="timeline-item" data-aos="fade-up" data-aos-delay="700">
                <div className="timeline-year">2022</div>
                <div className="timeline-content">
                  <h4>Innovation in Smart Solar Monitoring</h4>
                  <p>Introduced advanced smart monitoring systems for better performance tracking</p>
                </div>
              </div>
              <div className="timeline-item" data-aos="fade-up" data-aos-delay="800">
                <div className="timeline-year">2023</div>
                <div className="timeline-content">
                  <h4>Achieved 5 MW Installation Milestone</h4>
                  <p>Reached the significant milestone of 5 MW total installations</p>
                </div>
              </div>
              <div className="timeline-item" data-aos="fade-up" data-aos-delay="900">
                <div className="timeline-year">2024</div>
                <div className="timeline-content">
                  <h4>Expanded to North India (Delhi & Haryana)</h4>
                  <p>Extended our services to northern states including Delhi and Haryana</p>
                </div>
              </div>
              <div className="timeline-item" data-aos="fade-up" data-aos-delay="1000">
                <div className="timeline-year">2025</div>
                <div className="timeline-content">
                  <h4>Launched Green Energy Awareness Program</h4>
                  <p>Started comprehensive awareness program to promote renewable energy adoption</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* We are Certified */}
        <section className="section section-alt certifications-section">
          <div className="container">
            <div className="section-header text-center">
              <div className="section-badge">
                <i className="fas fa-certificate"></i>
                <span>Certifications</span>
              </div>
              <h2>We Are Certified</h2>
              <p>Trusted by government bodies and certified by leading organizations</p>
            </div>
            <div className="certifications-grid">
              <div className="certification-item" data-aos="fade-up">
                <div className="cert-logo">
                  <img src="/images/certifications/Syura Gujrat.png" alt="Surya Gujarat Certification" />
                </div>
                <h4>Surya Gujarat</h4>
              </div>
              <div className="certification-item" data-aos="fade-up" data-aos-delay="100">
                <div className="cert-logo">
                  <img src="/images/certifications/Guvnl.jpeg" alt="GUVNL Channel Partner" />
                </div>
                <h4>GUVNL Channel Partner</h4>
              </div>
              <div className="certification-item" data-aos="fade-up" data-aos-delay="200">
                <div className="cert-logo">
                  <img src="/images/certifications/Mnre.webp" alt="MNRE Approved" />
                </div>
                <h4>MNRE Approved Channel Partner</h4>
              </div>
              <div className="certification-item" data-aos="fade-up" data-aos-delay="300">
                <div className="cert-logo">
                  <img src="/images/certifications/FVSyPxxUEAAmPyI.png" alt="GEDA Certification" />
                </div>
                <h4>GEDA - Gujarat Energy Development Agency</h4>
              </div>
              <div className="certification-item" data-aos="fade-up" data-aos-delay="400">
                <div className="cert-logo">
                  <img src="/images/certifications/care rating.png" alt="CARE Ratings" />
                </div>
                <h4>CARE Ratings - Professional Risk Opinion</h4>
              </div>
              <div className="certification-item" data-aos="fade-up" data-aos-delay="500">
                <div className="cert-logo">
                  <img src="/images/certifications/MNREMinistry-logo.png" alt="Government of India Ministry of New and Renewable Energy" />
                </div>
                <h4>Government of India - Ministry of New and Renewable Energy</h4>
              </div>
            </div>
          </div>
        </section>

        {/* Certificate Carousel */}
        <CertificateCarousel />

        {/* Company Expertise */}
        <section className="section expertise-section">
          <div className="container">
            <div className="section-header text-center">
              <h2>Our Solar Solutions</h2>
              <p>We specialize in comprehensive solar energy systems and services</p>
            </div>
            <div className="expertise-grid">
              {expertise.map((item, index) => (
                <div key={index} className="expertise-card">
                  <div className="expertise-icon">
                    <i className={item.icon}></i>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <div className="technologies">
                    {item.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="section section-alt mission-vision">
          <div className="container">
            <div className="section-header text-center">
              <h2>Global Solar Energy</h2>
            </div>
            <div className="mission-vision-content">
              <div className="vision-card">
                <div className="card-icon">
                  <i className="fas fa-eye"></i>
                </div>
                <h3>Our Vision</h3>
                <p>
                  To become a leading solar company in India by offering reliable and affordable solar solutions.
                </p>
              </div>
              <div className="mission-card">
                <div className="card-icon">
                  <i className="fas fa-bullseye"></i>
                </div>
                <h3>Our Mission</h3>
                <ul className="mission-list">
                  <li>Empower more people to shift to clean, green solar energy</li>
                  <li>Deliver excellence and quality in every project</li>
                  <li>Grow with smart planning, innovation, and teamwork</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Company Values */}
        <section className="section values-section">
          <div className="container">
            <div className="section-header text-center">
              <h2>Our Core Values</h2>
            </div>
            <div className="core-values-list">
              <div className="core-value-item">
                <i className="fas fa-leaf"></i>
                <span>Clean Energy</span>
              </div>
              <div className="core-value-item">
                <i className="fas fa-heart"></i>
                <span>Trust & Transparency</span>
              </div>
              <div className="core-value-item">
                <i className="fas fa-award"></i>
                <span>Quality Work</span>
              </div>
              <div className="core-value-item">
                <i className="fas fa-clock"></i>
                <span>Timely Delivery</span>
              </div>
              <div className="core-value-item">
                <i className="fas fa-lightbulb"></i>
                <span>Innovation & Technology</span>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="section section-alt cta-section">
          <div className="container">
            <div className="cta-content text-center">
              <h2>Ready to Work With Us?</h2>
              <p>
                Let's discuss how we can help reduce your electricity bills and
                carbon footprint with our premium solar energy solutions.
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-primary">
                  Get In Touch
                </Link>
                <Link to="/services" className="btn btn-outline">
                  View Our Services
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;